import { socket } from "../api";
import { telemetry } from "../telemetry/telemetry";

type AnyHandler = (...args: any[]) => void;
type ReconnectHandler = () => void;
type EpochListener = (epoch: number) => void;

const reconnectHandlers = new Set<ReconnectHandler>();
const epochListeners = new Set<EpochListener>();
let booted = false;
let connectionEpoch = 0;

function notifyReconnect(reason: "connect" | "reconnect"): void {
  connectionEpoch += 1;
  for (const handler of reconnectHandlers) {
    try {
      handler();
    } catch (err) {
      console.error("[panel:reconnect_handler_error]", reason, err);
    }
  }
  for (const listener of epochListeners) {
    try {
      listener(connectionEpoch);
    } catch (err) {
      console.error("[panel:epoch_listener_error]", err);
    }
  }
}

export function bootSocketManager(): void {
  if (booted) return;
  booted = true;

  socket.on("connect", () => {
    telemetry("socket_connect", { id: socket.id });
    notifyReconnect("connect");
  });

  socket.on("disconnect", (reason: any) => {
    telemetry("socket_disconnect", { reason });
  });

  socket.on("connect_error", (err: any) => {
    telemetry("socket_connect_error", { message: err?.message });
  });

  // socket.io v4 expoe os eventos de reconnect via socket.io.on
  const manager = (socket as any).io;
  if (manager && typeof manager.on === "function") {
    manager.on("reconnect_attempt", (attempt: number) => {
      telemetry("socket_reconnect_attempt", { attempt });
    });
    manager.on("reconnect", (attempt: number) => {
      telemetry("socket_reconnect", { attempt });
      notifyReconnect("reconnect");
    });
    manager.on("reconnect_failed", () => {
      telemetry("socket_reconnect_failed");
    });
  }

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      telemetry("net_online");
      if (!socket.connected) {
        try {
          socket.connect();
        } catch (err) {
          console.error("[panel:socket_connect_failed]", err);
        }
      }
      notifyReconnect("reconnect");
    });
    window.addEventListener("offline", () => {
      telemetry("net_offline");
    });
  }
}

export function onReconnect(handler: ReconnectHandler): () => void {
  reconnectHandlers.add(handler);
  return () => {
    reconnectHandlers.delete(handler);
  };
}

export function subscribe(event: string, handler: AnyHandler): () => void {
  socket.on(event, handler);
  return () => {
    socket.off(event, handler);
  };
}

export function isHealthy(): boolean {
  return Boolean(socket.connected);
}

export function getConnectionEpoch(): number {
  return connectionEpoch;
}

export function onConnectionEpochChange(listener: EpochListener): () => void {
  epochListeners.add(listener);
  return () => {
    epochListeners.delete(listener);
  };
}
